"use client";

import type { AboutFormData } from "@/features/profile/tutor/schemas/profileEditSchemas.ts";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
// iconify icons are used per workspace rules
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnUnderline,
  BtnUndo,
  Editor,
  EditorProvider,
  Separator,
  Toolbar,
} from "react-simple-wysiwyg";
import { useEffect } from "react";

import { useProfileDataStore } from "../../../store/useProfileDataStore";

import { CustomStyleDropdown } from "./custom-style-dropdown";

import { getAboutSchema } from "@/features/profile/tutor/schemas/profileEditSchemas.ts";
import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { editTutorProfile } from "@/features/profile/tutor/api/editProfile.ts";
import { SubjectSelect } from "@/features/profile/tutor/components/profile-page/SubjectSelect.tsx";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InformationTooltip } from "@/components/InformationTooltip";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import { useProfileEditFooterStore } from "@/features/profile/tutor/store/useProfileEditFooterStore";

interface AboutEditProps {
  onSave: () => void;
}

export function AboutEdit({ onSave }: AboutEditProps) {
  const { profileData, setToReload } = useProfileDataStore();
  const data = profileData?.about_section;
  const redirect = useNavigate();
  const { t } = useTranslation(["profile", "common", "forms"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });
  const subjects = profileData?.subjects_section.map((subject) => subject.subject);
  const clearFooter = useProfileEditFooterStore((s) => s.clearFooter);

  useEffect(() => {
    clearFooter();
  }, [clearFooter]);
  const defaultSubjectIds = Object.entries(data?.main_subjects ?? []).map(
    ([_, { subject }]) => subject.id,
  );

  const form = useForm<AboutFormData>({
    resolver: zodResolver(getAboutSchema(t)),
    defaultValues: {
      description: data?.description || "",
    },
  });

  const {
    formState: { isDirty },
  } = form;

  const handleSubmit = async (values: AboutFormData) => {
    try {
      if (profileData) {
        await editTutorProfile(profileData?.id, "about", values);
        toast.success(t("common:success"), {
          description: t("forms:messages.success.profileUpdate"),
        });
        setToReload(true);
        redirect("");
        onSave();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("common:error"), {
        description: t("forms:messages.error.saving"),
      });
    }
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Form {...form}>
        <form className="flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="space-y-6">
            {/* Description Card */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-12 -right-16 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-10" />
              <div className="pointer-events-none absolute -bottom-10 -left-12 h-28 w-28 rounded-full bg-gradient-to-tr from-amber-400 to-pink-500 opacity-10" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-background/60 flex items-center rounded-xl p-2">
                    <Icon
                      className="text-accent-foreground"
                      height="1.125rem"
                      icon="mdi:card-account-details-outline"
                      width="1.125rem"
                    />
                  </div>
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:aboutSection.cards.description.title", {
                      defaultValue: "About me",
                    })}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:aboutSection.tooltips.description", {
                      defaultValue:
                        "Introduce yourself. Share your experience, specialties, and teaching style.",
                    })}
                  />
                </div>
                <CardDescription className="text-muted-foreground">
                  {t("profile:aboutSection.cards.description.description", {
                    defaultValue:
                      "Write a compelling description that helps students get to know you.",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="description"
                  render={() => (
                    <FormItem>
                      <FormLabel className="text-accent-foreground flex items-center gap-2 font-medium">
                        <div className="flex items-center justify-center p-1">
                          <Icon
                            className="text-accent-foreground"
                            height="1rem"
                            icon="mdi:draw-pen"
                            width="1rem"
                          />
                        </div>
                        {t("profile:aboutSectionEdit.description")}
                      </FormLabel>
                      <FormControl>
                        <div className="bg-muted/20 overflow-hidden rounded-xl backdrop-blur-sm">
                          <ScrollArea className="h-[40dvh] w-full">
                            <Controller
                              control={form.control}
                              name="description"
                              render={({ field }) => (
                                <EditorProvider>
                                  <div className="rsw-editor-wrapper">
                                    <style>{`
                              .rsw-editor {
                              border: none;
                              height: 100%;
       
                              }
                                .rsw-editor-wrapper .rsw-toolbar {
                                  background: #252628;
                                  backdrop-filter: blur(8px);
                                  border-bottom: 1px solid rgba(48, 49, 51, 0.3);
                                  padding: 0.75rem;
                                  display: flex;
                                  flex-wrap: wrap;
                                  gap: 0.25rem;
                                  height: 100%;
                                  border-radius: 0.75rem 0.75rem 0 0;
                                }
                                .rsw-editor-wrapper {
                                height:100% !important;
                                padding-top: 0px !important;
                                border-radius: 0.75rem;
                                overflow: hidden;
                                
                                } 

                                .rsw-editor-wrapper .rsw-toolbar button {
                                  height: 2.25rem;
                                  width: 2.25rem;
                                  border-radius: 0.5rem;
                                  display: inline-flex;
                                  align-items: center;
                                  justify-content: center;
                                  color: #ababab;
                                  background:#252628;
                                  border: 1px solid rgba(48, 49, 51, 0.3);
                                  cursor: pointer;
                                  transition: all 0.2s ease;
                                  backdrop-filter: blur(4px);
                                }

                                .rsw-editor-wrapper .rsw-toolbar button:hover {
                                  background: rgba(42, 44, 46, 0.5);
                                  border-color: rgba(48, 49, 51, 0.5);
                                  transform: translateY(-1px);
                                }

                                .rsw-editor-wrapper .rsw-toolbar button.rsw-active {
                                  background: #ececec;
                                  color: #161618;
                                  border-color: #ececec;
                                }
                                .rsw-btn[data-active=true]{
                                background: #ececec !important;
                                color: #161618 !important;
                                }

                                .rsw-editor-wrapper .rsw-separator {
                                  width: 1px;
                                  height: 2rem;
                                  background: rgba(48, 49, 51, 0.3);
                                  margin: 0 0.25rem;
                                }

                                .rsw-editor-wrapper .rsw-editor {
                                   color: inherit;
                                  font-size: 0.875rem;
                                  line-height: 1.6;
                                  min-height: 240px;
                                  background: rgb(22 22 24);
                                  backdrop-filter: blur(4px);
                                }

                                .rsw-editor-wrapper .rsw-editor:focus  {
                                  outline: none !important;
                                }
                                .rsw-ce:focus {
                                outline: none !important;
                                }

                                .rsw-editor-wrapper .rsw-editor h1 {
                                  font-size: 1.5rem;
                                  font-weight: 600;
                                  margin-bottom: 0.75rem;
                                   color: inherit;
                                }

                                .rsw-editor-wrapper .rsw-editor h2 {
                                  font-size: 1.25rem;
                                  font-weight: 600;
                                  margin-bottom: 0.5rem;
                                   color: inherit;
                                }

                                .rsw-editor-wrapper .rsw-editor h3 {
                                  font-size: 1.125rem;
                                  font-weight: 600;
                                  margin-bottom: 0.5rem;
                                   color: inherit;
                                }

                                .rsw-editor-wrapper .rsw-editor p {
                                  margin-bottom: 0.5rem;
                                }

                                .rsw-editor-wrapper .rsw-editor ul,
                                .rsw-editor-wrapper .rsw-editor ol {
                                  padding-left: 1.5rem;
                                  margin-bottom: 0.5rem;
                                }

                                .rsw-editor-wrapper .rsw-editor ul li {
                                  list-style-type: disc;
                                }

                                .rsw-editor-wrapper .rsw-editor ol li {
                                  list-style-type: decimal;
                                }

                                .rsw-editor-wrapper .rsw-editor blockquote {
                                  border-left: 3px solid #303133;
                                  margin-left: 0;
                                  margin-right: 0;
                                  padding-left: 1rem;
                                  color: #ababab;
                                  background: rgba(45, 46, 48, 0.2);
                                  border-radius: 0 0.5rem 0.5rem 0;
                                  padding: 1rem;
                                }

                                .rsw-editor-wrapper .rsw-editor pre {
                                  background: rgba(45, 46, 48, 0.5);
                                  border-radius: 0.5rem;
                                  padding: 0.75rem;
                                  font-family: monospace;
                                  overflow-x: auto;
                                  backdrop-filter: blur(4px);
                                }
                                    `}</style>
                                    <Editor
                                      className="h-full w-full"
                                      containerProps={{
                                        style: { height: "100%" },
                                        className: "rsw-editor-wrapper",
                                      }}
                                      value={field.value}
                                      onChange={(e) => field.onChange(e.target.value)}
                                    >
                                      <Toolbar>
                                        <BtnUndo />
                                        <BtnRedo />
                                        <Separator />
                                        <BtnBold />
                                        <BtnItalic />
                                        <BtnUnderline />
                                        <BtnStrikeThrough />
                                        <Separator />
                                        <BtnNumberedList />
                                        <BtnBulletList />
                                        <Separator />
                                        <BtnClearFormatting />
                                        <Separator />
                                        <CustomStyleDropdown />
                                      </Toolbar>
                                    </Editor>
                                  </div>
                                </EditorProvider>
                              )}
                            />
                          </ScrollArea>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Subjects Card */}
            <Card className="bg-side-accent/80 border-border/30 relative overflow-hidden">
              <div className="pointer-events-none absolute -top-10 -right-14 h-36 w-36 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 opacity-10" />
              <div className="pointer-events-none absolute -bottom-8 -left-10 h-28 w-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 opacity-10" />
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-background/60 flex items-center rounded-xl p-2">
                    <Icon
                      className="text-accent-foreground"
                      height="1.125rem"
                      icon="mdi:book-open-variant"
                      width="1.125rem"
                    />
                  </div>
                  <CardTitle className="text-accent-foreground text-lg">
                    {t("profile:aboutSection.cards.subjects.title", {
                      defaultValue: "Main subjects",
                    })}
                  </CardTitle>
                  <InformationTooltip
                    description={t("profile:aboutSection.tooltips.subjects", {
                      defaultValue: "Select the subjects you primarily teach.",
                    })}
                  />
                </div>
                <CardDescription className="text-muted-foreground">
                  {t("profile:aboutSection.cards.subjects.description", {
                    defaultValue: "These subjects may be highlighted on your profile.",
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="subject_ids"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-accent-foreground flex items-center gap-2 font-medium">
                        <Icon
                          className="text-accent-foreground"
                          height="1.125rem"
                          icon="mdi:book-open-variant"
                          width="1.125rem"
                        />
                        {t("profile:aboutSectionEdit.subjects")}
                      </FormLabel>
                      <FormControl>
                        <div className="bg-muted/20 relative z-50 rounded-xl p-4 backdrop-blur-sm">
                          <SubjectSelect
                            defaultSubjectIds={defaultSubjectIds}
                            subjects={subjects ?? []}
                            onSelect={(ids) => field.onChange(ids)}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="mb-8 flex justify-end gap-3 pt-6">
            <Button
              className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
              variant="outline"
              onClick={onSave}
            >
              {t("common:cancel")}
            </Button>
            <Button
              className="bg-accent-foreground/90 hover:bg-accent-foreground text-background rounded-xl"
              disabled={!isDirty}
              type="submit"
            >
              {form.formState.isSubmitting ? t("common:saving") : t("common:save")}
            </Button>
          </div>
        </form>
      </Form>
    </motion.div>
  );
}
